import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StarShape, SunShape, SwanShape, LotusShape, ShapeBadge } from "@/components/base/BrandShapes";
import DNAHelix from "@/components/base/DNAHelix";
import BrandAccentLine from "@/components/base/BrandAccentLine";
import { usePublicHome } from "@/hooks/useCMSHome";
import { getLocalized } from "@/utils/localization";

export default function WhyUs() {
  const { i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { content } = usePublicHome();
  const { whyUs } = content;

  const dimensionShapes = [
    {
      Shape: StarShape,
      label: isAr ? "الاطباء" : "Clinical Depth",
      sub: isAr ? "الأطباء · الخبرة الطبية · التقييم المتخصص" : "More time · Thorough evaluation",
      desc: isAr ? "النجمة ترمز إلى الخبرة الطبية والتميز، والتوجيه نحو رعاية موثوقة تبدأ بفهم الحالة قبل اتخاذ القرار." : "The star represents medical expertise and guidance toward trusted care",
    },
    {
      Shape: SunShape,
      label: isAr ? "توازن الرشاقة" : "Technical Depth",
      sub: isAr ? "السمنة · الأيض · إدارة الوزن" : "DNA · Biological age · Metabolism",
      desc: isAr ? "البجعة ترمز إلى التوازن والرشاقة، وتعكس نهجًا صحيًا متكاملًا لإدارة الوزن وتحسين الصحة الأيضية" : "The sun symbolizes vitality, energy, and ongoing health resilience",
    },
    {
      Shape: SwanShape,
      label: isAr ? "عمق جمالي" : "Metabolic Balance",
      sub: isAr ? "الجمال · النضارة · العناية المتخصصة" : "Obesity · MASLD · Metabolic health",
      desc: isAr ? "الشمس ترمز إلى الحيوية والقوة والنشاط الصحي المستمر، وتعكس أهمية دعم صحة العظام والعضلات للحفاظ على الحركة والقدرة على ممارسة الحياة بنشاط." : "The swan symbolizes equilibrium and graceful weight wellness",
    },
    {
      Shape: LotusShape,
      label: isAr ? "عمق الطاقة والحيوية" : "Global Depth",
      sub: isAr ? "العظام · العضلات · النشاط" : "Second Opinion · USA · Korea",
      desc: isAr ? "اللوتس يرمز للتجدد والصحة الداخلية وانسجام الجسد والعقل" : "The lotus represents renewal, inner vitality, and mind-body harmony",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const badge = getLocalized(i18n.language, whyUs.badge, whyUs.badge_en);
  const heading = getLocalized(i18n.language, whyUs.heading, whyUs.heading_en);
  const subHeading = getLocalized(i18n.language, whyUs.subHeading, whyUs.subHeading_en);
  const description = getLocalized(i18n.language, whyUs.description, whyUs.description_en);
  const subDescription = getLocalized(i18n.language, whyUs.subDescription, whyUs.subDescription_en);

  return (
    <section ref={ref} className="py-24 bg-white overflow-hidden relative">
      {/* DNA Helix decorative */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none hidden xl:block opacity-30">
        <DNAHelix width={50} height={500} color1="#C8A96E" color2="#2E4E45" rungs={12} speed={0.6} opacity={1} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* ── Brand Identity Section ── */}
        <div className="text-center mb-16">
          <span className="inline-block bg-brand-forest-100 text-brand-forest-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            {isAr ? "هويتنا البصرية" : "Our Visual Identity"}
          </span>
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">
            {isAr ? "أربعة رموز، فلسفة واحدة" : "Four Symbols, One Philosophy"}
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed">
            {isAr
              ? "تجتمع هذه الرموز لتشكّل لغة بصرية للعافية حيث تتعايش العلم، الحيوية، التوازن، والجمال في انسجام تام"
              : "These symbols unite to form a visual lexicon of wellness where science, vitality, balance, and elegance coexist in complete harmony"}
          </p>
        </div>

        {/* Four Shapes Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {dimensionShapes.map(({ Shape, label, sub, desc }, i) => (
            <div
              key={label}
              className={`text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="flex justify-center mb-5">
                <div className="relative group cursor-default">
                  <div className="absolute inset-0 rounded-full bg-brand-cream-300/20 scale-0 group-hover:scale-125 transition-transform duration-500 opacity-0 group-hover:opacity-100" />
                  <ShapeBadge size={80} className="hover:bg-brand-forest-600 transition-all duration-300 animate-glow-forest group-hover:scale-110">
                    <div className="animate-brand-morph" style={{ animationDelay: `${i * 0.8}s` }}>
                      <Shape size={38} color="rgba(227,218,201,0.95)" />
                    </div>
                  </ShapeBadge>
                </div>
              </div>
              <h3 className="font-black text-brand-forest-800 text-sm mb-1">{label}</h3>
              <p className="text-brand-forest-600 text-xs font-medium mb-2">{sub}</p>
              <p className="text-gray-400 text-xs leading-relaxed px-2">{desc}</p>
            </div>
          ))}
        </div>

        {/* Animated brand accent divider */}
        <div className="flex items-center gap-4 mb-16">
          <BrandAccentLine className="flex-1" height={2} animated />
          <div className="animate-brand-morph">
            <StarShape size={20} color="#2E4E45" className="opacity-60" />
          </div>
          <BrandAccentLine className="flex-1" height={2} color1="#C8A96E" color2="#2E4E45" animated />
        </div>

        {/* ── Why Us Content ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden border border-brand-cream-200" style={{ height: "500px" }}>
              <img
                src={whyUs.mainImage}
                alt="Precision Medicine"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-900/40 to-transparent"></div>
            </div>
            {/* Floating Card 1 */}
            <div className="absolute -bottom-6 -left-4 bg-white rounded-2xl p-4 border border-brand-cream-200 w-52">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-cream-100 rounded-xl">
                  <SunShape size={22} color="#2E4E45" />
                </div>
                <div>
                  <p className="text-lg font-black text-gray-900">90–120</p>
                  <p className="text-xs text-gray-400">{isAr ? "دقيقة لكل جلسة" : "Minutes / Session"}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{isAr ? "نمنح الحالة الوقت الذي تستحقه" : "Giving each case the time it deserves"}</p>
            </div>
            {/* Floating Card 2 */}
            <div className="absolute -top-5 -right-4 bg-brand-forest-800 rounded-2xl p-4 text-white w-44">
              <div className="mb-2">
                <LotusShape size={28} color="rgba(227,218,201,0.9)" />
              </div>
              <p className="text-sm font-black text-white">{isAr ? "امتداد دولي" : "Global Reach"}</p>
              <p className="text-xs text-brand-cream-300/80 mt-1">{isAr ? "USA · Korea · وأكثر" : "USA · Korea · & More"}</p>
            </div>
          </div>

          {/* Right: Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block bg-brand-forest-100 text-brand-forest-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
              {badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
              {heading}
              <span className="text-brand-forest-600"> {subHeading}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-3">
              {description}
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">
              {subDescription}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyUs.items.map((item, i) => (
                <div
                  key={i}
                  className={`flex gap-4 p-4 rounded-2xl border border-brand-cream-200 bg-brand-cream-50/50 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{ transitionDelay: `${(i + 4) * 100}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-brand-cream-100 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-brand-forest-600 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm mb-1">{getLocalized(i18n.language, item.title, item.title_en)}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{getLocalized(i18n.language, item.description, item.description_en)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Three dimensions */}
            <div className="mt-8 p-5 rounded-2xl bg-brand-cream-50 border border-brand-cream-200">
              <p className="text-xs font-bold text-brand-forest-700 uppercase tracking-wide mb-4">
                {isAr ? "ثلاثة أبعاد متكاملة" : "Three Integrated Dimensions"}
              </p>
              <div className="flex flex-wrap gap-4">
                {[
                  { Shape: StarShape, label: isAr ? "عمق سريري" : "Clinical Depth", sub: isAr ? "وقت أطول · تقييم غير سريع" : "More time · Unhurried care" },
                  { Shape: SunShape,  label: isAr ? "عمق تقني" : "Technical Depth",  sub: isAr ? "DNA · عمر بيولوجي · أيضي" : "DNA · Bio-Age · Metabolic" },
                  { Shape: LotusShape,label: isAr ? "عمق دولي" : "Global Reach",   sub: isAr ? "Second Opinion · USA · Korea" : "Second Opinion · USA · Korea" },
                ].map(({ Shape, label, sub }) => (
                  <div key={label} className="flex items-center gap-2.5 flex-1 min-w-[120px]">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <Shape size={20} color="#2E4E45" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-forest-800">{label}</p>
                      <p className="text-xs text-brand-forest-600/70 mt-0.5">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
